import { useEffect, useRef } from 'react';

/**
 * Persists a view's filter/result snapshot to the injected storage (getItem/setItem) so it
 * survives navigating to other tabs and back, or a reload. `apply` is called once on mount with
 * the stored snapshot (if any); after that every change to `snapshot` is written back.
 */
export function usePersistedViewState<T>(
  storage: any,
  key: string,
  snapshot: T,
  apply: (stored: Partial<T>) => void
): void {
  const loadedRef = useRef(false);
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useEffect(() => {
    loadedRef.current = false;
    if (!storage) {
      return;
    }
    let cancelled = false;

    void (async () => {
      try {
        const raw = await storage.getItem(key);
        if (!cancelled && raw) {
          applyRef.current(JSON.parse(raw) as Partial<T>);
        }
      } catch (err) {
        console.warn(`usePersistedViewState: failed to restore ${key}`, err);
      } finally {
        if (!cancelled) {
          loadedRef.current = true;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storage, key]);

  const serialized = JSON.stringify(snapshot);
  useEffect(() => {
    // Don't overwrite the stored snapshot with the empty initial state before it's been restored.
    if (!storage || !loadedRef.current) {
      return;
    }
    void Promise.resolve(storage.setItem(key, serialized)).catch((err: unknown) => {
      console.warn(`usePersistedViewState: failed to save ${key}`, err);
    });
  }, [storage, key, serialized]);
}
